"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Gatherings() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const events = [{
    day: "23",
    month: "SEP 2026",
    category: "CERCLE MAMAN & BEBE 0-4 MOIS - I EDITION",
    title: "Cercle maman & bebe 0-4 mois",
    meta: "1 x semaine pendant 6 semaines • NYON",
    image: "https://slow-mama.com/wp-content/uploads/2026/07/4bf2028d-e654-4c44-b88d-75a935f0b97b.png?v=ibluncf2ri",
    srcset: "https://slow-mama.com/wp-content/uploads/2026/07/4bf2028d-e654-4c44-b88d-75a935f0b97b.png?v=2q61ts2oewg",
    sizes: "https://slow-mama.com/wp-content/uploads/2026/07/4bf2028d-e654-4c44-b88d-75a935f0b97b.png?v=6b1cmu4ah6b",
    alt: "Préparation de plats sains et nourrissants dans une cuisine lumineuse"
  }, {
    day: "20",
    month: "JAN 2027",
    category: "CERCLE MAMAN & BEBE 0-4 MOIS - II EDITION",
    title: "Cercle maman & bebe 0-4 mois",
    meta: "1 x semaine pendant 6 semaines • NYON",
    image: "https://slow-mama.com/wp-content/uploads/2026/07/4bf2028d-e654-4c44-b88d-75a935f0b97b.png?v=q090ej8lu3n",
    srcset: "https://slow-mama.com/wp-content/uploads/2026/07/4bf2028d-e654-4c44-b88d-75a935f0b97b.png?v=9hjk2x4ex",
    sizes: "https://slow-mama.com/wp-content/uploads/2026/07/4bf2028d-e654-4c44-b88d-75a935f0b97b.png?v=12vlynh1smxi",
    alt: "Mères assises en cercle dans un espace chaleureux et minimaliste"
  }];
  return <section data-section-id="2328" id="prochaines-rencontres" className="bg-[var(--ivory)] text-[var(--brun)] py-16 px-6 md:py-24 md:px-10 lg:px-16">
      <div ref={ref} className={`mx-auto max-w-7xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="mb-6 flex items-center" aria-hidden="true">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-[var(--caramel)]">
                  <path d="M14 3c-3 3-5 5-5 8 0 2 1 3.5 2.5 4.5C10 17 8 19 8 22c2 0 4-1 5.2-2.5L14 25l.8-5.5C16 21 18 22 20 22c0-3-2-5-3.5-6.5C18 14.5 19 13 19 11c0-3-2-5-5-8z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" />
                </svg>
              </div>

              <p className="font-default text-[0.875rem] tracking-[0.2em] text-[var(--caramel)] mb-6 uppercase font-semibold">
                &mdash; À venir
              </p>

              <h2 className="font-serif font-normal text-[var(--brun)] text-3xl md:text-4xl lg:text-5xl leading-[1.2] tracking-tight mb-8">Nos prochains <span className="italic text-[var(--caramel)]" data-ast-id="f:/src/pages/Circle/sections/Gatherings.tsx|sig:i29ucw" data-ast-start="4071" data-ast-end="4267" contenteditable="false" style={{}}>cercles</span></h2>

              <p className="font-default text-lg md:text-xl text-[var(--brun)] leading-relaxed mb-10 max-w-md">Six semaines pour rencontrer d’autres mamans, poser tes questions et être accompagnée par des spécialistes autour du postpartum.</p>

              <Button asChild variant="outline" size="lg" className="font-default text-[0.875rem] tracking-[0.1em] uppercase border-[var(--caramel)] bg-[var(--caramel)] text-[var(--off-white)] hover:bg-[var(--brun)] hover:border-[var(--brun)] rounded-lg px-8 h-12">
                <Link href="#pied-de-page">iNSCRIPTIOn
              </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="flex flex-col gap-5 md:gap-6">
              {events.map((event, i) => <li key={i} data-index={i} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)} className="group relative">
                  {i === 0 && <div className="h-px w-full bg-[var(--beige)]" />}

                  <Link to="/ContactPage" className={`flex flex-col md:flex-row md:items-center gap-5 md:gap-6 py-8 md:py-10 transition-transform duration-500 ease-out ${hoveredIndex === i ? "md:translate-x-2" : ""}`}>
                    <div className="flex md:flex-col md:items-start items-baseline gap-3 md:gap-1 md:w-20 shrink-0">
                      <span className="font-default text-5xl md:text-6xl leading-none text-[var(--beige)]">
                        {event.day}
                      </span>
                      <span className="font-default text-[0.75rem] tracking-[0.2em] text-[var(--caramel)] uppercase font-semibold">
                        {event.month}
                      </span>
                    </div>

                    <div className="shrink-0 w-24 overflow-hidden rounded-lg">
                      <div className="aspect-[4/5] w-full overflow-hidden rounded-lg">
                        <img src={event.image} data-wvc-srcset={event.srcset} data-wvc-sizes={event.sizes} alt={event.alt} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-700 ease-out ${hoveredIndex === i ? "scale-105" : "scale-100"}`} />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-default text-[0.75rem] tracking-[0.2em] text-[var(--caramel)] uppercase mb-3 font-semibold">
                        {event.category}
                      </p>
                      <h3 className="font-default font-normal text-xl md:text-2xl text-[var(--brun)] leading-snug mb-3">
                        {event.title}
                      </h3>
                      <p className="font-default text-[0.75rem] tracking-[0.1em] text-[var(--brun)] uppercase">
                        {event.meta}
                      </p>
                    </div>

                    <div className="hidden md:flex shrink-0 items-center justify-center">
                      
                    </div>
                  </Link>

                  <div className={`h-px w-full transition-all duration-500 ${hoveredIndex === i ? "bg-[var(--caramel)] h-[1px]" : "bg-[var(--beige)]"}`} />
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}